import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../store/useStore';
import ProductCard from './Products/ProductCard';
import Loader from './Loader';
import { FaArrowRight, FaLeaf } from 'react-icons/fa';

const ProductGridSkeleton = () => (
  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 sm:gap-6">
    {[...Array(10)].map((_, i) => (
      <div key={i} className="animate-pulse rounded-3xl border border-gray-100 bg-white shadow-sm overflow-hidden">
        <div className="aspect-square bg-gray-200/70 w-full" />
        <div className="p-4 space-y-3">
          <div className="h-3 w-20 rounded-full bg-gray-200" />
          <div className="h-4 w-full rounded-full bg-gray-200" />
          <div className="h-4 w-3/4 rounded-full bg-gray-200" />
          <div className="h-5 w-1/2 rounded-full bg-gray-200" />
          <div className="grid grid-cols-2 gap-2 pt-1">
            <div className="h-9 rounded-xl bg-gray-200" />
            <div className="h-9 rounded-xl bg-gray-200" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

const SORT_OPTIONS = [
  { value: 'default',    label: 'Default' },
  { value: 'price-low',  label: 'Price: Low to High' },
  { value: 'price-high', label: 'Price: High to Low' },
  { value: 'discount',   label: 'Best Discount' },
];

const ProductGrid = () => {
  const { getFilteredProducts, selectedCategory, selectedCategoryData, categories, products_loading, products_loaded } = useStore();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState('default');
  const [visibleCount, setVisibleCount] = useState(12);

  const products = getFilteredProducts();
  const { products: allProducts } = useStore.getState();
  const displayProducts =
    products.length === 0 && selectedCategory && selectedCategory !== 'all'
      ? allProducts
      : products;

  const sortedProducts = [...displayProducts].sort((a, b) => {
    if (sortBy === 'price-low')  return a.price - b.price;
    if (sortBy === 'price-high') return b.price - a.price;
    if (sortBy === 'discount')   return b.discount - a.discount;
    return 0;
  });

  const visibleProducts = sortedProducts.slice(0, visibleCount);

  const getCategoryName = () => {
    if (!selectedCategory || selectedCategory === 'all') return 'All Products';
    if (selectedCategoryData?.name) return selectedCategoryData.name;
    const match = categories.find((c) => String(c.category_id) === String(selectedCategory));
    return match?.name || 'Featured Products';
  };

  return (
    <section className="bg-gradient-to-b from-gray-50 to-white py-14 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-amber-500">
              Popular Picks
            </span>
            <h2 className="mt-2 text-3xl font-black tracking-tight text-gray-900 sm:text-4xl">
              {getCategoryName()}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {sortedProducts.length} product{sortedProducts.length !== 1 ? 's' : ''} available
            </p>
          </div>

          {/* Sort dropdown */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="w-full rounded-2xl border-2 border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700 shadow-sm transition-all hover:border-primary-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 sm:w-auto sm:min-w-[200px]"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* States */}
        {products_loading || !products_loaded ? (
          <>
            <Loader compact label="Loading products" sublabel="Checking the latest product availability." />
            <ProductGridSkeleton />
          </>
        ) : visibleProducts.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-4 rounded-3xl border border-dashed border-gray-200 bg-white py-24 text-center">
            <span className="flex h-20 w-20 items-center justify-center rounded-full bg-primary-50 text-primary-500">
              <FaLeaf className="h-9 w-9" aria-hidden="true" />
            </span>
            <h3 className="text-xl font-bold text-gray-900">No products found</h3>
            <p className="text-sm text-gray-500">Try adjusting your filters or browse all categories.</p>
            <button
              onClick={() => navigate('/products')}
              className="mt-2 rounded-full border-2 border-primary-500 px-6 py-2.5 text-sm font-bold text-primary-600 hover:bg-primary-50"
            >
              Browse All
            </button>
          </div>
        ) : (
          <>
            {/* Mobile swipe hint */}
            <div className="mb-4 flex items-center justify-between sm:hidden">
              <p className="text-xs text-gray-400">Showing {visibleProducts.length} of {sortedProducts.length}</p>
              <button onClick={() => navigate('/products')} className="text-xs font-bold text-primary-600">
                <span className="inline-flex items-center gap-1">
                  View all
                  <FaArrowRight className="h-3 w-3" aria-hidden="true" />
                </span>
              </button>
            </div>

            {/* Product grid */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 sm:gap-6">
              {visibleProducts.map((product) => (
                <ProductCard key={product.id ?? product.product_id} product={product} />
              ))}
            </div>

            {/* Load more */}
            {visibleCount < sortedProducts.length && (
              <div className="mt-12 text-center">
                <button
                  onClick={() => setVisibleCount((prev) => prev + 12)}
                  className="group inline-flex items-center gap-3 rounded-full border-2 border-gray-200 bg-white px-10 py-4 text-sm font-bold text-gray-700 shadow-sm transition-all hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 hover:shadow-md"
                >
                  Load More Products
                  <svg className="h-4 w-4 transition-transform group-hover:translate-y-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default ProductGrid;
